import { useState, useMemo } from 'react';
import { reservationsApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@verlo/shared/components/ui/card';
import { Button } from '@verlo/shared/components/ui/button';
import { Badge } from '@verlo/shared/components/ui/badge';
import { toast } from 'sonner';
import { 
  CalendarDays, ChevronLeft, ChevronRight, Users,
} from 'lucide-react';
import { STATUS_CONFIG } from './BookingStatusActions';

const HOUR_SLOTS = [];
for (let h = 12; h <= 22; h++) {
  HOUR_SLOTS.push(`${h.toString().padStart(2, '0')}:00`);
}

const WeekViewCalendar = ({ reservations, selectedDate, onDateChange, onReservationClick }) => {
  const weekStart = useMemo(() => {
    const d = new Date(selectedDate);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }, [selectedDate]);

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push({
        date: d.toISOString().split('T')[0],
        label: d.toLocaleDateString('en-GB', { weekday: 'short' }),
        dayNum: d.getDate(),
        isToday: d.toISOString().split('T')[0] === new Date().toISOString().split('T')[0],
        isSelected: d.toISOString().split('T')[0] === selectedDate,
      });
    }
    return days;
  }, [weekStart, selectedDate]);

  const navigateWeek = (direction) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + (direction * 7));
    onDateChange(d.toISOString().split('T')[0]);
  };

  const getReservationsForSlot = (date, hour) => {
    const hourInt = parseInt(hour.replace(':', ''));
    return reservations.filter(r => {
      if (r.date !== date) return false;
      if (['cancelled', 'no_show'].includes(r.status)) return false;
      const rTime = parseInt(r.time.replace(':', ''));
      return rTime >= hourInt && rTime < hourInt + 100;
    });
  };

  const monthLabel = useMemo(() => {
    const start = weekDays[0];
    const end = weekDays[6];
    const startMonth = new Date(start.date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const endMonth = new Date(end.date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    return startMonth === endMonth ? startMonth : `${new Date(start.date).toLocaleDateString('en-GB', { month: 'short' })} - ${endMonth}`;
  }, [weekDays]);

  return (
    <Card className="border shadow-sm" data-testid="week-view-calendar">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays size={18} />
            Week View
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} data-testid="week-prev">
              <ChevronLeft size={14} />
            </Button>
            <span className="text-sm font-medium min-w-[160px] text-center">{monthLabel}</span>
            <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} data-testid="week-next">
              <ChevronRight size={14} />
            </Button>
            <Button 
              variant="outline" size="sm"
              onClick={() => onDateChange(new Date().toISOString().split('T')[0])}
              className="text-xs"
            >
              Today
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[700px]">
            <thead>
              <tr>
                <th className="w-16 p-2 text-xs text-ink-soft border-b border-r bg-surface-2 sticky left-0">Time</th>
                {weekDays.map(day => (
                  <th 
                    key={day.date}
                    className={`p-2 text-center border-b border-r cursor-pointer transition-colors ${
                      day.isToday ? 'bg-verlo/5' : day.isSelected ? 'bg-signal-info-tint' : 'bg-surface-2'
                    }`}
                    onClick={() => onDateChange(day.date)}
                    data-testid={`week-day-${day.date}`}
                  >
                    <div className="text-xs text-ink-soft">{day.label}</div>
                    <div className={`text-sm font-semibold ${
                      day.isToday ? 'text-verlo' : 'text-ink'
                    }`}>
                      {day.dayNum}
                    </div>
                    {day.isToday && (
                      <div className="w-1.5 h-1.5 bg-verlo rounded-full mx-auto mt-0.5" />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HOUR_SLOTS.map(slot => (
                <tr key={slot}>
                  <td className="p-1.5 text-[11px] text-ink-faint border-r bg-surface-2/50 text-center sticky left-0 border-b">{slot}</td>
                  {weekDays.map(day => {
                    const slotReservations = getReservationsForSlot(day.date, slot);
                    return (
                      <td 
                        key={`${day.date}-${slot}`}
                        className={`border-r border-b p-0.5 align-top min-h-[40px] h-10 ${
                          day.isToday ? 'bg-verlo/[0.02]' : ''
                        }`}
                      >
                        {slotReservations.map(r => {
                          const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
                          return (
                            <div
                              key={r.id}
                              className="bg-verlo text-white rounded px-1 py-0.5 text-[9px] mb-0.5 cursor-pointer hover:bg-verlo-hover truncate"
                              onClick={() => {
                                onDateChange(day.date);
                                onReservationClick(r);
                              }}
                              title={`${r.customer_name} (${r.party_size}) - ${r.time} - ${sc.label}`}
                              data-testid={`week-res-${r.id}`}
                            >
                              <span className="font-medium">{r.time}</span> {r.customer_name} <Users size={8} className="inline" />{r.party_size}
                            </div>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default WeekViewCalendar;
